import * as React from 'react';
import { FunctionComponent, useRef, useState, useEffect } from 'react';
import Header from '../../components/Header';
import { Footer } from "../../components/Footer";
import { Input } from "../../components/Input";
import { CheckBox } from "../../components/CheckBox";
import { UserSchema, UsersService } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { useFormAndValidation } from "../../hooks/useFormAndValidation";

const ProfilePage: FunctionComponent = () => {
    const { user, setUser } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [avatarError, setAvatarError] = useState('');
    const [avatarSaved, setAvatarSaved] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileSaved, setProfileSaved] = useState(false);

    const { values, handleChange, errors, isValid, resetForm } = useFormAndValidation();

    useEffect(() => {
        if (user) {
            resetForm({
                username: user.username || '',
                about: user.about || '',
                subscribed: user.subscribed ?? true,
            });
        }
    }, [user, resetForm]);

    const hasChanges = user && (
        values.username !== (user.username || '') ||
        values.about !== (user.about || '') ||
        values.subscribed !== (user.subscribed ?? true)
    );

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarPreview(URL.createObjectURL(file));
            setAvatarError('');
            setAvatarSaved(false);
        }
    };

    const handleAvatarUpload = async () => {
        const file = fileInputRef.current?.files?.[0];
        if (!file) return;

        setAvatarLoading(true);
        setAvatarError('');

        try {
            const updatedUser = await UsersService.uploadAvatar({ avatar: file });
            setUser(updatedUser);
            setAvatarPreview(null);
            setAvatarSaved(true);
            setTimeout(() => setAvatarSaved(false), 2000);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
            setAvatarError(err.message || 'Ошибка загрузки');
        } finally {
            setAvatarLoading(false);
        }
    };

    const handleProfileSave = async () => {
        if (!hasChanges) return;

        setProfileLoading(true);
        setProfileSaved(false);

        try {
            const updatedUser = await UsersService.updateUser({
                username: values.username,
                about: values.about,
                subscribed: values.subscribed,
            });
            setUser(updatedUser);
            setProfileSaved(true);
            setTimeout(() => setProfileSaved(false), 2000);
        } catch (err: any) {
            console.error(err);
        } finally {
            setProfileLoading(false);
        }
    };

    return (
        <>
            <Header />
            <div className='profile'>
                <form className='form form_dark'>
                    <h2 className='form__header form__header_center'>Редактировать профиль</h2>

                    {/* Аватар */}
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24}}>
                        <img
                            style={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                marginBottom: 16,
                            }}
                            src={avatarPreview || user?.avatar || require('../../images/ava.png')}
                            alt="Аватар"
                        />

                        <div className='profile-page__avatar-actions'>
                            <label className='profile-page__file-label'>
                                Выбрать файл
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                    className='profile-page__file-input'
                                />
                            </label>

                            {avatarPreview && (
                                <button
                                    type="button"
                                    className={`form__submit${avatarLoading ? ' form__submit_loading' : ''}`}
                                    style={{marginTop: 16, marginBottom: 0}}
                                    onClick={handleAvatarUpload}
                                    disabled={avatarLoading}
                                >
                                    {avatarLoading ? 'Загрузка' : avatarSaved ? 'Сохранено ✓' : 'Сохранить аватар'}
                                </button>
                            )}

                            {avatarError && (
                                <p className='profile-page__error'>{avatarError}</p>
                            )}
                        </div>

                        {avatarError && (
                            <span className='form__input-error form__input-error_active'
                                  style={{position: 'static', marginTop: 4}}>
                            {avatarError}
                        </span>
                        )}
                    </div>

                    {/* Форма профиля */}
                    <fieldset className='form__set'>
                        <Input
                            title='Имя'
                            name='username'
                            minLength={2}
                            maxLength={40}
                            onChange={handleChange}
                            error={errors.username}
                            value={values.username || ''}
                            dark={true}
                        />
                        <Input
                            title='О себе'
                            name='about'
                            minLength={2}
                            maxLength={200}
                            onChange={handleChange}
                            error={errors.about}
                            value={values.about || ''}
                            dark={true}
                        />
                    </fieldset>

                    <fieldset className='form__set'>
                        <CheckBox
                            name='subscribed'
                            type='checkbox'
                            onChange={handleChange}
                            error={errors.subscribed}
                            checked={values.subscribed ?? true}
                            dark={true}
                            required={false}
                        >
                            Подписка на email-рассылку
                        </CheckBox>
                    </fieldset>

                    <button
                        type="button"
                        className={`form__submit${!hasChanges || !isValid ? ' form__submit_disabled' : ''}${profileLoading ? ' form__submit_loading' : ''}${profileSaved ? ' form__submit_loading-ok' : ''}`}
                        style={{marginTop: 24}}
                        onClick={handleProfileSave}
                        disabled={!hasChanges || !isValid || profileLoading}
                    >
                        {profileLoading ? 'Сохранение' : profileSaved ? 'Сохранено' : 'Сохранить'}
                    </button>
                </form>
            </div>
            <Footer />
        </>
    );
};

export { ProfilePage };